import type {ReactNode} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

/**
 * The props for the `StickyActionButton` component.
 * Configures a CTA button with icon and label. Positioning (floating above the bottom
 * navigation on mobile, sticky within a desktop sidebar) is handled by the caller's wrapper,
 * not by this component - see `CtaSlot` and `SidebarGroup` in the walk detail page.
 */
interface ComponentProps {
    /** Whether the button is disabled. */
    disabled?: boolean;
    /** An optional icon element rendered before the label. */
    icon?: ReactNode;
    /** The button label text. */
    label: string;
    /** Click handler. */
    onClick?(): void;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'StickyActionButton'
     */
    testId?: string;
}

/**
 * A full-width CTA button with icon and label. Uses the primary gradient with a deep shadow for
 * the Stitch "Unlock Tour" design. Renders as a plain, unpositioned button - the caller is
 * responsible for any floating/sticky positioning of its wrapper.
 *
 * @param props          The component props.
 * @param props.disabled Whether the button is disabled.
 * @param props.icon     An optional icon before the label.
 * @param props.label    The button label text.
 * @param props.testId   An optional test ID used for automation selectors. Defaults to `'StickyActionButton'`.
 * @param props.onClick  Click handler.
 * @returns A JSX element rendering the StickyActionButton.
 *
 * @example
 * ```tsx
 * <StickyActionButton
 *     icon={<LockOpenIcon />}
 *     label="Unlock Tour (€4.99)"
 *     onClick={handleUnlock}
 * />
 * ```
 */
const StickyActionButton = ({
    disabled = false,
    icon,
    label,
    onClick,
    testId = 'StickyActionButton'
}: ComponentProps) => (
    <Wrapper>
        <Button
            data-cy={testId}
            disabled={disabled}
            type="button"
            onClick={onClick}
        >
            {icon && <IconSlot>{icon}</IconSlot>}
            {label}
        </Button>
    </Wrapper>
);

StickyActionButton.displayName = 'StickyActionButton';

export {StickyActionButton};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.div`
    padding: 0 ${spacing(6)};
`;

const Button = styled.button`
    align-items: center;
    background: linear-gradient(135deg, ${({theme}) => theme.colors.primaryDefault}, ${({theme}) => theme.colors.primaryLight});
    border: none;
    border-radius: 9999px;
    box-shadow: 0 8px 30px rgba(21, 66, 18, 0.3);
    color: ${({theme}) => theme.colors.white};
    cursor: pointer;
    display: flex;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.8rem;
    font-weight: 700;
    gap: ${spacing(3)};
    justify-content: center;
    padding: ${spacing(5)};
    transition: transform 0.15s;
    width: 100%;

    &:active {
        transform: scale(0.97);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const IconSlot = styled.span`
    align-items: center;
    display: inline-flex;
`;